///
/// EarthBrowser
///
/// Created by Matt Giger on 4/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Anchored callout for the hovered or focused entity: places the card
/// beside the entity's screen position, keeps it on screen, draws the
/// connector, and hands focused React content types to the anchored
/// React panel. The hover card itself is the shared HoverCard template
/// fed by buildHoverCardSpec, so this file owns placement, not drawing.
///

import { anchoredReactPanelDismissEventName, anchoredReactPanelMeasuredEventName, emitAnchoredReactPanelState } from "@/earthbrowser/design-system/AnchoredReactPanelBridge"
import { cReactPanelContentTypes, reactPanelSize } from "@/earthbrowser/design-system/anchoredPanelSizes"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { isMobile } from "@/earthbrowser/utils/platform"
import { getViewportInsets } from "@/earthbrowser/utils/viewportInsets"
import { closeInfoWindow } from "@/redux/userInterfaceSlice"
import { appStore } from "@/redux/store"
import { type ActivityBase } from "@stovokor/engine/ActivityBase"
import type { FocusTarget } from "@stovokor/gestures/FocusTarget"
import { type vec3 } from "@stovokor/math"
import { Canvas } from "@stovokor/uipanes/Canvas"
import { Pane } from "@stovokor/uipanes/Pane"
import { CanvasStyles } from "./CanvasStyles"
import { DesignSpacing } from "./DesignSpacing"
import { cHoverCardWidth, HoverCardCanvas } from "./HoverCard"
import { buildHoverCardSpec, cHoverAccent } from "./hoverCardSpecs"
import { SelectionLayerDepth, SelectionPriority, type Selectable } from "./SelectionCoordinator"

/** Placeholder height before a spec has been measured */
const cHoverCardMinHeight = 92
const SCREEN_MARGIN = 18
const CONNECTOR_OFFSET = 22
const REACT_PANEL_CONNECTOR_OFFSET = 220
const POSITION_THRESHOLD = 6

interface FocusedObjectCalloutOptions {
	ownsAnchoredReactPanel?: boolean
}

type CalloutPlacement = "right" | "left" | "above" | "below"

type AnchoredFocusTarget = FocusTarget & InfoPanelProvider & {
	transform: {
		position: vec3
	}
}

function hasWorldAnchor(target: FocusTarget & InfoPanelProvider): target is AnchoredFocusTarget {
	if (!target || typeof target !== "object" || !("transform" in target)) {
		return false
	}

	const transform = (target as { transform?: { position?: unknown } }).transform
	const position = transform?.position as { x?: unknown; y?: unknown; z?: unknown } | undefined
	return typeof position?.x === "number" &&
		typeof position?.y === "number" &&
		typeof position?.z === "number"
}

class CalloutConnectorCanvas extends Canvas {
	private anchorX: number = -1
	private anchorY: number = -1
	private panelX: number = 0
	private panelY: number = 0
	private panelWidth: number = cHoverCardWidth
	private panelHeight: number = cHoverCardMinHeight
	private accentColor: string = cHoverAccent.default

	public updateConnector(
		anchorX: number,
		anchorY: number,
		panelX: number,
		panelY: number,
		panelWidth: number,
		panelHeight: number,
		accentColor: string
	): void {
		this.anchorX = anchorX
		this.anchorY = anchorY
		this.panelX = panelX
		this.panelY = panelY
		this.panelWidth = panelWidth
		this.panelHeight = panelHeight
		this.accentColor = accentColor
		this.setNeedsUpdate()
	}

	override render(): void {
		this.clear()
		if (this.anchorX < 0 || this.anchorY < 0) {
			return
		}

		CanvasStyles.renderConnectorLine(
			this,
			this.anchorX,
			this.anchorY,
			this.panelX,
			this.panelY,
			this.panelWidth,
			this.panelHeight,
			{ lineWidth: 1.25, color: this.accentColor, withGlow: true }
		)

		const context = this.context
		context.beginPath()
		context.arc(this.anchorX, this.anchorY, 4, 0, Math.PI * 2)
		context.fillStyle = this.accentColor
		context.fill()
		context.strokeStyle = "rgba(15, 23, 42, 0.7)"
		context.lineWidth = 1
		context.stroke()
	}
}

class CalloutConnectorOverlay extends Pane {
	private readonly connectorCanvas: CalloutConnectorCanvas
	private readonly activity: ActivityBase

	constructor(context: EarthBrowserContext, activity: ActivityBase) {
		super(context)
		this.activity = activity
		this.connectorCanvas = new CalloutConnectorCanvas()
		this._contents = this.connectorCanvas
		this.offset(0, 0)
		this.size(window.innerWidth, window.innerHeight)
	}

	public updateConnector(
		anchorX: number,
		anchorY: number,
		panelX: number,
		panelY: number,
		panelWidth: number,
		panelHeight: number,
		accentColor: string
	): void {
		const screenWidth = this.activity.engine.world.camera.projection.width
		const screenHeight = this.activity.engine.world.camera.projection.height
		if (this.width !== screenWidth || this.height !== screenHeight) {
			this.size(screenWidth, screenHeight)
		}

		this.connectorCanvas.updateConnector(anchorX, anchorY, panelX, panelY, panelWidth, panelHeight, accentColor)
	}
}

export class FocusedObjectCallout extends Pane implements Selectable {
	private readonly activity: ActivityBase
	private readonly calloutCanvas: HoverCardCanvas
	private readonly connectorOverlay: CalloutConnectorOverlay
	private readonly ownsAnchoredReactPanel: boolean

	private currentTarget?: AnchoredFocusTarget
	private focusedTarget?: AnchoredFocusTarget
	private hoveredTarget?: AnchoredFocusTarget
	private frameTaskId: number = -1
	private targetX: number = 0
	private targetY: number = 0
	private currentX: number = 0
	private currentY: number = 0
	private currentPlacement: CalloutPlacement = "right"
	private initializedPosition = false
	private currentAccentColor = cHoverAccent.default
	private displayMode: "focus" | "hover" | null = null
	private currentPanelData?: InfoPanelData
	private currentRenderMode: "engine" | "react" = "engine"
	// Actual rendered height reported by the React card (ResizeObserver in
	// AnchoredPanelShell); overrides the nominal size-table height so the
	// connector line ends at the real card edge
	private measuredPanelHeight?: number
	private readonly handleDismiss: () => void
	private readonly handleMeasuredHeight: (event: Event) => void

	constructor(context: EarthBrowserContext, activity: ActivityBase, options: FocusedObjectCalloutOptions = {}) {
		super(context)
		this.activity = activity
		this.ownsAnchoredReactPanel = options.ownsAnchoredReactPanel ?? true
		this.visible = false
		this.size(cHoverCardWidth, cHoverCardMinHeight)
		this.calloutCanvas = new HoverCardCanvas()
		this._contents = this.calloutCanvas

		this.connectorOverlay = new CalloutConnectorOverlay(context, activity)
		this.connectorOverlay.visible = false

		this.handleDismiss = () => {
			if (this.ownsAnchoredReactPanel && this.focusedTarget) {
				this.hide()
				appStore.dispatch(closeInfoWindow())
			}
		}
		window.addEventListener(anchoredReactPanelDismissEventName, this.handleDismiss)

		this.handleMeasuredHeight = (event: Event) => {
			if (!this.ownsAnchoredReactPanel) {
				return
			}
			const height = (event as CustomEvent<number>).detail
			if (typeof height === "number" && height > 0) {
				this.measuredPanelHeight = height
			}
		}
		window.addEventListener(anchoredReactPanelMeasuredEventName, this.handleMeasuredHeight)

		context.selectionCoordinator.register(this, SelectionPriority.FOCUS)
		context.selectionCoordinator.addNode(this.connectorOverlay, SelectionLayerDepth.CONNECTOR)
		context.selectionCoordinator.addNode(this, SelectionLayerDepth.FOCUS_WINDOW)
	}

	public showFor(target: FocusTarget & InfoPanelProvider): void {
		if (!hasWorldAnchor(target)) {
			this.hide()
			return
		}

		// New target: forget the previous card's measured height until the
		// freshly mounted card reports its own
		if (this.focusedTarget !== target) {
			this.measuredPanelHeight = undefined
		}
		this.focusedTarget = target
		// Focus supersedes any in-flight preview of the same target
		this.hoveredTarget = undefined
		this.refreshDisplay(true)
	}

	public hide(): void {
		// Clear both targets: the hover-preview instance keeps its target in
		// hoveredTarget, so clearing only focusedTarget would leave the hover
		// tooltip up (e.g. next to the info panel after clicking to focus)
		this.focusedTarget = undefined
		this.hoveredTarget = undefined
		this.refreshDisplay()
	}

	public previewFor(target: FocusTarget & InfoPanelProvider): void {
		if (!hasWorldAnchor(target)) {
			return
		}

		this.hoveredTarget = target
		this.refreshDisplay(this.currentTarget !== target)
	}

	public hidePreview(target?: FocusTarget): void {
		if (!this.hoveredTarget) {
			return
		}

		if (!target || this.hoveredTarget === target) {
			this.hoveredTarget = undefined
			this.refreshDisplay()
		}
	}

	public hideFromCoordinator(): void {
		const context = this.context as EarthBrowserContext
		if (this.focusedTarget && context.focusManager.getFocusedNode()) {
			context.focusManager.releaseFocus({ preserveCurrentState: true })
			return
		}

		this.hide()
	}

	private startPositionUpdates(): void {
		if (this.frameTaskId >= 0) {
			return
		}

		this.frameTaskId = this.activity.addFrameTask(() => {
			this.updatePosition()
		})
	}

	private stopPositionUpdates(): void {
		if (this.frameTaskId < 0) {
			return
		}

		this.activity.removeFrameTask(this.frameTaskId)
		this.frameTaskId = -1
	}

	private updatePosition(force = false): void {
		if (!this.currentTarget) {
			return
		}

		// Refresh live data from the target (updates satellite position/speed each frame)
		if (this.displayMode === "focus" && this.currentPanelData?.contentType === "satellite") {
			const freshData = this.currentTarget.getInfoPanelData()
			if (freshData) {
				this.currentPanelData = freshData
				this.calloutCanvas.setSpec(buildHoverCardSpec(freshData))
			}
		}

		const worldPosition = this.currentTarget.transform.position
		const screenPosition = this.activity.engine.world.camera.worldToScreen(worldPosition)
		// Place within the part of the canvas the chrome leaves visible: the
		// data dock covers the right (or bottom) edge, and a card placed
		// under it would be unreadable
		const insets = getViewportInsets()
		const screenWidth = this.activity.engine.world.camera.projection.width - insets.right
		const screenHeight = this.activity.engine.world.camera.projection.height - insets.bottom
		const nextRenderMode = this.determineRenderMode()
		const { width: panelWidth, height: panelHeight } = this.getActivePanelSize(nextRenderMode)
		this.size(panelWidth, panelHeight)

		// Mobile React panels render as a bottom sheet (AnchoredPanelShell):
		// no placement search, no connector line - the sheet docks itself.
		// The React side ignores x/y; only `visible` matters here.
		if (nextRenderMode === "react" && isMobile()) {
			this.currentRenderMode = nextRenderMode
			this.visible = false
			this.calloutCanvas.setSpec(undefined)
			this.connectorOverlay.visible = false
			this.emitAnchoredPanelState(panelWidth, panelHeight)
			return
		}

		const offset = nextRenderMode === "react" ? REACT_PANEL_CONNECTOR_OFFSET : CONNECTOR_OFFSET
		this.currentPlacement = this.pickPlacement(screenPosition.x, screenPosition.y, screenWidth, screenHeight, panelWidth, panelHeight, offset)

		const unclamped = this.getPlacementPosition(screenPosition.x, screenPosition.y, this.currentPlacement, panelWidth, panelHeight, offset)
		const nextTargetX = Math.max(SCREEN_MARGIN, Math.min(screenWidth - panelWidth - SCREEN_MARGIN, unclamped.x))
		const nextTargetY = Math.max(SCREEN_MARGIN, Math.min(screenHeight - panelHeight - SCREEN_MARGIN, unclamped.y))

		if (
			force ||
			!this.initializedPosition ||
			Math.abs(nextTargetX - this.targetX) > POSITION_THRESHOLD ||
			Math.abs(nextTargetY - this.targetY) > POSITION_THRESHOLD
		) {
			this.targetX = nextTargetX
			this.targetY = nextTargetY
		}

		if (!this.initializedPosition || force) {
			this.currentX = this.targetX
			this.currentY = this.targetY
			this.initializedPosition = true
		} else {
			this.currentX += (this.targetX - this.currentX) * DesignSpacing.SMOOTH_FACTOR
			this.currentY += (this.targetY - this.currentY) * DesignSpacing.SMOOTH_FACTOR
		}

		this.currentRenderMode = nextRenderMode
		const showEngineCallout = this.currentRenderMode === "engine" && this.displayMode !== "focus"
		this.visible = showEngineCallout
		if (!showEngineCallout) {
			this.calloutCanvas.setSpec(undefined)
		}
		this.offset(this.currentX, this.currentY)
		this.connectorOverlay.updateConnector(
			screenPosition.x,
			screenPosition.y,
			this.currentX,
			this.currentY,
			panelWidth,
			panelHeight,
			this.currentAccentColor
		)
		this.emitAnchoredPanelState(panelWidth, panelHeight)
	}

	private refreshDisplay(forcePositionReset = false): void {
		const nextTarget = this.focusedTarget ?? this.hoveredTarget
		const nextMode: "focus" | "hover" | null = this.focusedTarget
			? "focus"
			: this.hoveredTarget
				? "hover"
				: null

		if (!nextTarget || !nextMode) {
			this.transitionSelectionMode(null)
			this.currentTarget = undefined
			this.currentPanelData = undefined
			this.measuredPanelHeight = undefined
			this.displayMode = null
			this.currentRenderMode = "engine"
			this.visible = false
			this.connectorOverlay.visible = false
			this.calloutCanvas.setSpec(undefined)
			this.stopPositionUpdates()
			this.initializedPosition = false
			this.connectorOverlay.updateConnector(-1, -1, 0, 0, cHoverCardWidth, cHoverCardMinHeight, this.currentAccentColor)
			if (this.ownsAnchoredReactPanel) {
				// Hide event: size is unused while invisible
				emitAnchoredReactPanelState({ visible: false, x: 0, y: 0, width: 0, height: 0 })
			}
			return
		}

		const panelData = nextTarget.getInfoPanelData()
		if (!panelData) {
			if (nextMode === "focus") {
				this.focusedTarget = undefined
			} else {
				this.hoveredTarget = undefined
			}
			this.refreshDisplay(forcePositionReset)
			return
		}

		const spec = buildHoverCardSpec(panelData)
		const targetChanged = this.currentTarget !== nextTarget
		const modeChanged = this.displayMode !== nextMode

		this.transitionSelectionMode(nextMode)

		this.currentTarget = nextTarget
		this.currentPanelData = panelData
		this.displayMode = nextMode
		this.currentAccentColor = spec.accent
		this.calloutCanvas.setSpec(spec)
		this.connectorOverlay.visible = true
		if (forcePositionReset || targetChanged || modeChanged) {
			this.initializedPosition = false
		}

		this.startPositionUpdates()
		this.updatePosition(forcePositionReset || targetChanged || modeChanged)
	}

	private transitionSelectionMode(nextMode: "focus" | "hover" | null): void {
		const context = this.context as EarthBrowserContext
		if (this.displayMode === "focus" && nextMode !== "focus") {
			context.selectionCoordinator.willHide(this, SelectionPriority.FOCUS)
		}
		if (this.displayMode !== "focus" && nextMode === "focus") {
			context.selectionCoordinator.willShow(this, SelectionPriority.FOCUS)
		}
	}

	private determineRenderMode(): "engine" | "react" {
		const contentType = this.currentPanelData?.contentType
		if (this.displayMode !== "focus" || !contentType || !cReactPanelContentTypes.has(contentType)) {
			return "engine"
		}

		return "react"
	}

	private emitAnchoredPanelState(panelWidth: number, panelHeight: number): void {
		if (!this.ownsAnchoredReactPanel) {
			return
		}

		const contentType = this.currentPanelData?.contentType
		const shouldShowReactPanel = this.displayMode === "focus" &&
			this.currentRenderMode === "react" &&
			!!contentType &&
			cReactPanelContentTypes.has(contentType)

		emitAnchoredReactPanelState({
			visible: shouldShowReactPanel,
			x: this.currentX,
			y: this.currentY,
			width: panelWidth,
			height: panelHeight
		})
	}

	private getActivePanelSize(renderMode: "engine" | "react"): { width: number; height: number } {
		// Focused React panels: the shared size table (compact on mobile)
		// covers every react content type, including wildfire and hurricane
		if (renderMode === "react") {
			const size = reactPanelSize(this.currentPanelData?.contentType)
			if (size) {
				// Prefer the height the React card actually rendered at, so
				// placement and the connector line track variable content
				if (this.measuredPanelHeight !== undefined) {
					return { width: size.width, height: this.measuredPanelHeight }
				}
				return size
			}
		}

		// Engine hover card: one template, measured from its spec
		const height = this.calloutCanvas.measuredHeight()
		return { width: cHoverCardWidth, height: height > 0 ? height : cHoverCardMinHeight }
	}

	private pickPlacement(
		anchorX: number,
		anchorY: number,
		screenWidth: number,
		screenHeight: number,
		panelWidth: number,
		panelHeight: number,
		offset: number = CONNECTOR_OFFSET
	): CalloutPlacement {
		const placements: CalloutPlacement[] = [this.currentPlacement, "right", "left", "above", "below"]
		const uniquePlacements = placements.filter((placement, index) => placements.indexOf(placement) === index)

		for (const placement of uniquePlacements) {
			if (this.fitsOnScreen(anchorX, anchorY, placement, screenWidth, screenHeight, panelWidth, panelHeight, offset)) {
				return placement
			}
		}

		return this.currentPlacement
	}

	private fitsOnScreen(
		anchorX: number,
		anchorY: number,
		placement: CalloutPlacement,
		screenWidth: number,
		screenHeight: number,
		panelWidth: number,
		panelHeight: number,
		offset: number = CONNECTOR_OFFSET
	): boolean {
		const position = this.getPlacementPosition(anchorX, anchorY, placement, panelWidth, panelHeight, offset)

		return (
			position.x >= SCREEN_MARGIN &&
			position.y >= SCREEN_MARGIN &&
			position.x + panelWidth <= screenWidth - SCREEN_MARGIN &&
			position.y + panelHeight <= screenHeight - SCREEN_MARGIN
		)
	}

	private getPlacementPosition(
		anchorX: number,
		anchorY: number,
		placement: CalloutPlacement,
		panelWidth: number,
		panelHeight: number,
		offset: number = CONNECTOR_OFFSET
	): { x: number; y: number } {
		switch (placement) {
			case "left":
				return {
					x: anchorX - panelWidth - offset,
					y: anchorY - panelHeight / 2
				}
			case "above":
				return {
					x: anchorX - panelWidth / 2,
					y: anchorY - panelHeight - offset
				}
			case "below":
				return {
					x: anchorX - panelWidth / 2,
					y: anchorY + offset
				}
			case "right":
			default:
				return {
					x: anchorX + offset,
					y: anchorY - panelHeight / 2
				}
		}
	}

	override destroy(): void {
		this.focusedTarget = undefined
		this.hoveredTarget = undefined
		this.refreshDisplay()
		if (this.ownsAnchoredReactPanel) {
			// Hide event: size is unused while invisible
			emitAnchoredReactPanelState({ visible: false, x: 0, y: 0, width: 0, height: 0 })
		}

		window.removeEventListener(anchoredReactPanelDismissEventName, this.handleDismiss)
		window.removeEventListener(anchoredReactPanelMeasuredEventName, this.handleMeasuredHeight)

		const context = this.context as EarthBrowserContext
		context.selectionCoordinator.unregister(this, SelectionPriority.FOCUS)
		context.selectionCoordinator.removeNode(this.connectorOverlay)
		context.selectionCoordinator.removeNode(this)

		super.destroy()
	}
}
